import type { Ref } from '@wix/materializer'
import { createRef } from '@wix/materializer'
import type { GhostPropertiesItem } from '@wix/thunderbolt-becky-root'
import type { LayoutDataItems } from '@wix/thunderbolt-becky-types'
import { pointers } from '@wix/thunderbolt-becky-types'
import {
	createExperimentRef,
	createRefInResolvedRefsNamespaces,
	isGhostPropertyItem,
	isHiddenInAllVariants,
} from '../../../byRefUtils'
import { createByRefsisConsumptionCompNode } from '../../../compNodeUtils'
import { Component } from '@wix/thunderbolt-becky-root'
import { getTemplateFromInflatedId } from '../../../../utils'
import { LAYOUT_INFLATED_MAP, MOBILE_PROPERTY_SUFFIX, SITE_NAMESPACE, STRUCTURE_ASPECT } from '../../../constants'

export type isGhostCompToViewItem = {
	isGhostComp: boolean
	inflatedStructure?: Component
	isHiddenInAllVariants?: boolean
}

type MaybeGhostPropertyItem = GhostPropertiesItem | undefined
type IsGhostComponentDeps = {
	templateComp: Ref<Component>
	hostOverrideDesktopPropertyItem: Ref<MaybeGhostPropertyItem>
	hostOverrideMobilePropertyItem: Ref<MaybeGhostPropertyItem> | null
	propertyItem: Ref<MaybeGhostPropertyItem>
	layoutDataItems: Ref<Array<LayoutDataItems>> | null
	ghostifyHiddenComps: Ref<boolean>
}

const {
	data: {
		DATA_MAPS: { PROPERTIES },
	},
	components: {
		PROPERTY_TYPES: { PROPERTY_QUERY },
	},
} = pointers

export const isGhostComponent = createByRefsisConsumptionCompNode<
	IsGhostComponentDeps,
	isGhostCompToViewItem | undefined
>({
	getDependencies: (templateCompId, { id, layoutQuery }, _ancestorRefCompId, scopedCreateRef, isMobileView) => {
		const propertyQuery = `${id}-${PROPERTY_QUERY}`
		const mobilePropertyQuery = `${id}-${PROPERTY_QUERY}${MOBILE_PROPERTY_SUFFIX}`
		return {
			templateComp: scopedCreateRef<Component>(STRUCTURE_ASPECT, templateCompId),
			hostOverrideDesktopPropertyItem: createRef<MaybeGhostPropertyItem>([
				SITE_NAMESPACE,
				PROPERTIES,
				propertyQuery,
			]),
			hostOverrideMobilePropertyItem: isMobileView
				? createRef<MaybeGhostPropertyItem>([SITE_NAMESPACE, PROPERTIES, mobilePropertyQuery])
				: null,
			propertyItem: createRefInResolvedRefsNamespaces<MaybeGhostPropertyItem>(
				PROPERTIES,
				isMobileView ? mobilePropertyQuery : propertyQuery
			),
			layoutDataItems: layoutQuery
				? createRefInResolvedRefsNamespaces<Array<LayoutDataItems>>(LAYOUT_INFLATED_MAP, layoutQuery)
				: null,
			ghostifyHiddenComps: createExperimentRef('specs.thunderbolt.ghostify_hidden_comps'),
		}
	},
	toViewItem: (
		inflatedStructure,
		{
			templateComp,
			hostOverrideDesktopPropertyItem,
			hostOverrideMobilePropertyItem,
			propertyItem,
			layoutDataItems,
			ghostifyHiddenComps,
		}
	) => {
		if (!templateComp) {
			return
		}

		if (
			isGhostPropertyItem(hostOverrideDesktopPropertyItem) ||
			isGhostPropertyItem(hostOverrideMobilePropertyItem || propertyItem)
		) {
			return {
				// inflatedStructure is needed in onInvalidation to be added to byRefModel for byRef Api.
				// It's added at this stage since structure node is dependent on isGhostComp and once it's computed, its structure is already overwritten.
				// In addition, we override the id with the original compId because that's what byRef Api expects.
				isGhostComp: true,
				inflatedStructure: {
					...inflatedStructure,
					id: getTemplateFromInflatedId(inflatedStructure.id) || inflatedStructure.id,
				},
			}
		}

		if (ghostifyHiddenComps && layoutDataItems && isHiddenInAllVariants(layoutDataItems)) {
			return {
				isGhostComp: true,
				isHiddenInAllVariants: true,
			}
		}

		return { isGhostComp: false }
	},
})
